import { surfaceUrl } from '../zones';
import { cachedTranscript, forgetTranscript, rememberTranscript, sweepTranscripts } from './transcript-cache';
import { foldTranscript } from './transcript-fold';
import { RunTranscript } from './run-transcript';
import { RunTree } from './run-tree';
import { durableTranscriptStore } from './conversation';
import { resumeAbandonedWork } from './driver';
import { ConversationsStore } from './conversations-store';
import { conversationsDeleted, conversationsTouched, onConversationChange } from './conversation-events';
import { loadModelCatalog } from './model-catalog';
import { clientToolAnnouncements, clientToolDeclarations, dispatchClientTool, isClientTool, onClientToolsChanged, } from './tools';
import { setProviderLogoBase } from '../elements/vitesse-provider-logo';
/**
 * "The org's AI configuration changed" — published by whoever changed it, heard by whoever shows it.
 *
 * The console and the AI rail can be the SAME page: change the default model in Configuration and
 * the composer beside it is instantly stale, still offering the model that was the default a second
 * ago. A reload would fix it, which is not an answer.
 *
 * A plain event rather than a manager reference, because the publisher (a config view) must not have
 * to know a manager exists, and there may be none or several.
 */
export const aiConfigEvents = new EventTarget();
import { registerMentionProviders, specialistMentions } from './mentions';
import { readScreen } from './screen';
/*
 * AiManager — main-thread PROXY of the AI plane (the AI web worker holds the real IO + state). Same
 * manager-pattern shape every component knows: it exposes state + re-emits `change`, and Lit
 * subscribes via addEventListener('change', …). APP-GLOBAL, not per-gateway — an AI conversation is
 * per-USER, so this is created once (like FleetManager / CameraStreamManager), not inside a
 * GatewayEntry.
 *
 * FIRST increment: the handshake (auth → session config + the minted ephemeral realtime token, with
 * the prompt locked into the token server-side). The conversation append/subscribe + the driver loop
 * layer on top, holding their own state here.
 */
export class AiManager extends EventTarget {
    /** What is waiting on a yes. Rendered by the chat as inline approval cards. */
    get pendingConfirms() {
        return this.confirms;
    }
    /** The specialists this surface may talk to, as the handshake resolved them.
     *  A getter rather than a copy: a re-handshake replaces the session, and a
     *  snapshot handed out earlier would pin a roster that no longer exists. */
    get specialists() {
        return this.session.experts;
    }
    /** The person's answer for one pending call. Unknown id is a no-op (already settled or timed out). */
    resolveConfirm(id, approve) {
        this.confirmWaiters.get(id)?.(approve);
    }
    /**
     * A STOPPED RUN'S WRITES ARE DECLINED, not left waiting for a click.
     *
     * The card stayed on screen after the run that asked was stopped, and approving it then ran the
     * write on behalf of work the person had already called off. Declined rather than dropped, so the
     * call's own refusal path runs and the trail says the write was not approved. Only these runs':
     * another agent's card in the same tab is still a live question.
     */
    declineConfirms(runIds) {
        const stopped = new Set(runIds);
        for (const c of this.confirms)
            if (stopped.has(this.confirmRuns.get(c.id) ?? ''))
                this.resolveConfirm(c.id, false);
    }
    /**
     * Park one call in the queue until a person answers, the turn is a casualty of time, or nobody
     * is looking.
     *
     * THE TIMEOUT IS THE RENDERLESS-HOST STORY: a surface with no chat open (voice without the rail,
     * a headless embed) shows no card, so the promise must not hang the agent loop forever. Two
     * minutes is long enough to read what is being asked and short enough that an abandoned tab does
     * not hold a turn open all afternoon; expiry counts as a refusal, because silence is not consent.
     */
    awaitConfirm(call, runId) {
        return new Promise((resolve) => {
            const done = (approve) => {
                // Settle exactly once — the timeout and a click can race, and the waiter's presence is the
                // tie-breaker. Whoever loses finds the entry gone and does nothing.
                if (!this.confirmWaiters.delete(call.id))
                    return;
                this.confirmRuns.delete(call.id);
                clearTimeout(timer);
                this.confirms = this.confirms.filter((c) => c.id !== call.id);
                this.dispatchEvent(new CustomEvent('change'));
                resolve(approve);
            };
            const timer = setTimeout(() => done(false), 120_000);
            this.confirmWaiters.set(call.id, done);
            if (runId)
                this.confirmRuns.set(call.id, runId);
            this.confirms = [...this.confirms, { id: call.id, name: call.name, argumentsJson: call.argumentsJson }];
            this.dispatchEvent(new CustomEvent('change'));
        });
    }
    constructor(ai, platform = 'app', // the connecting surface (app. / fleet. / ai.) — its own persona
    /**
     * Where the AI plane lives, and how to authenticate to it.
     *
     * Needed on the MAIN thread as well as in the worker: a chat turn streams directly from here
     * (the edge adapter reads the response as it arrives), which a Comlink round-trip per token
     * cannot do. The worker keeps its own copy for the handshake and the archive writes.
     *
     * Optional so the existing constructor calls keep working; a manager without it can still
     * handshake and list, and only `open()` needs it.
     */
    wire) {
        super();
        this.ai = ai;
        this.platform = platform;
        this.wire = wire;
        // Starts 'connecting': the manager is created only to immediately handshake — the honest initial
        // state is "establishing", not "error".
        this.session = {
            connState: 'connecting',
            ephemeralToken: null,
            tokenError: '',
            model: '',
            textModel: '',
            imageModel: '',
            experts: [],
            creditRefused: false,
            serverTools: [],
            confirmTools: [],
            standsIn: {},
            // Closed until the handshake says otherwise — a capability nobody has answered for yet is not
            // a capability that is on.
            codeExecutionEnabled: false,
        };
        /*
         * THE APPROVAL QUEUE — Ask-tier calls waiting on a person.
         *
         * STATE, not a callback. The first design was an `onConfirmTool` hook a host was supposed to wire
         * with its own dialog — and no host ever did, so every Ask-tier tool was refused the instant the
         * model reached for it and the user never saw a question. Ask behaved as Ban with extra steps.
         * As manager state the prompt exists BY DEFAULT: any surface rendering the conversation renders
         * the pending cards, and a surface that renders nothing costs a timeout rather than an instant
         * silent refusal.
         *
         * Immutably replaced on every change so a Lit host re-renders on identity, like every other
         * manager-pattern list in the app.
         */
        this.confirms = [];
        this.confirmWaiters = new Map();
        /** Whose call each card is — a chat turn or an agent — so a stop can decline exactly its own. Kept
         *  beside the cards rather than on them: a card is the call as the model sent it, and nothing else. */
        this.confirmRuns = new Map();
        /** The thread currently open, or null while the user is composing or at the hub. */
        this.conversation = null;
        /**
         * The recents data layer, created ONCE and kept.
         *
         * Kept on the manager rather than inside the list widget so the cache survives opening a
         * conversation and coming back — the list is not re-fetched for a round trip the user made in two
         * seconds. It also means a redesigned widget inherits the paging and the count for free.
         */
        this.conversations = new ConversationsStore((cursor) => this.ai.api.listConversations(this.platform, cursor));
        /** True once a handshake has been made — so a config change refreshes a LIVE session rather than
         *  opening one on a rail nobody has touched. */
        this.started = false;
        this.stopWatchingTools = onClientToolsChanged(() => this.scheduleAnnounce());
        /**
         * Run one tool call, wherever it lives.
         *
         * LOCAL FIRST, then the server. A client tool has no server implementation to fall through to, so
         * trying it first is the only order that can work; and it keeps the common case — a UI acting on
         * its own screen — off the network entirely.
         *
         * ALWAYS RETURNS AN OUTCOME, never throws. A thrown error here would leave a recorded call with
         * no result, which the next turn has to read as UNKNOWN rather than failed — and on this platform
         * "unknown" is the reading that stops a write to plant being repeated. Every path below produces
         * a sentence the model can act on.
         */
        /*
         * REPEATING A CALL THAT ALREADY FAILED IS NOT PROGRESS.
         *
         * A model handed a failure retries it — reasonably, since most failures are transient. What it
         * cannot see is that it has now made the SAME call, with the same arguments, and been refused the
         * same way, several times. Observed live: seven identical `dashboards.create` calls against a
         * broker that was refusing the write, ~20s each, two and a half minutes of a person's time and
         * the whole step budget spent learning nothing.
         *
         * Counted by (tool, arguments) so it is genuinely the same call: a retry with DIFFERENT arguments
         * is the model reacting to what it was told, which is exactly the behaviour to leave alone. Only
         * failures count, and any success clears the entry — a tool that works intermittently is not a
         * tool being thrashed.
         *
         * Structural rather than a line in the prompt, because a prompt is advice a model may follow when
         * it is not under pressure, and this fires precisely when it is.
         */
        this.failedTwice = new Map();
        /** Count a failure, forget a success. Every outcome goes through here so no path can skip it. */
        /** The last answer each exact call got in its run — so a repeat can be told it is one. */
        this.lastAnswer = new Map();
        this._account = '';
        /**
         * The handshake, shaped for `VoiceWindowKeeper`.
         *
         * Handed to the keeper rather than the keeper being built here, because a keeper needs a live
         * session to read usage from and to speak a refusal through — and that session belongs to whoever
         * owns the audio, not to this manager. This supplies only the server half.
         */
        this.voiceHandshake = async (body) => {
            const s = await this.ai.api.handshake(this.platform, { previousWindow: body.previousWindow }, clientToolAnnouncements());
            return {
                // A refused window is `ok: false` even though the HTTP call succeeded — the keeper's question
                // is "may the call continue", not "did the request work".
                ok: !s.creditRefused && s.connState === 'connected',
                message: s.tokenError,
                holdId: s.holdId,
                windowMs: s.windowMs,
                renewAtMs: s.renewAtMs,
                creditRefused: s.creditRefused,
            };
        };
        // The specialists, as @ mentions. Every surface has a roster, so this is the one provider
        // base-ui registers itself; read through the getter, so a re-handshake's roster is the one offered.
        this.mentionsOff = registerMentionProviders(specialistMentions(() => this.specialists));
        /*
         * A RELOAD MID-ANSWER IS A STOP, AND IS RECORDED AS ONE.
         *
         * The answer streaming into the thread lives only in this tab until the turn ends; a reload,
         * a navigation, a closed tab took it with them — the person watched three paragraphs arrive
         * and the reopened thread had none of them, not even a note that something had been cut.
         * `pagehide` (which fires for all of those, and for mobile backgrounding, where `beforeunload`
         * does not) stops the turn the way the Stop button does, and the append it issues survives
         * the document because the transcript client sends it keepalive.
         */
        globalThis.addEventListener?.('pagehide', () => {
            // A question nobody will answer is a refusal: the tab that held the card is going. Settled
            // FIRST, so the call's own refusal path runs — its audit row and its transcript result say
            // "needed approval and did not get it" instead of the reload finding a call that simply
            // never reported back, which on this platform reads as "go and check the controller".
            for (const c of this.confirms)
                this.resolveConfirm(c.id, false);
            if (this.conversation?.busy)
                this.conversation.stop('pagehide');
        });
        /*
         * Pull the LIVE model catalog — the roster and its prices — on construction.
         *
         * Here rather than in each app's boot, because "remember to call this" is a rule that gets
         * followed once: the console fetched it and every other surface silently served the baked
         * fallback, so the same picker showed twenty-odd priced vendors in one window and four unpriced
         * ones in another. Any surface with a manager now gets the same roster by construction.
         *
         * Fire-and-forget and failure-tolerant: the baked catalog is a working fallback, so a slow or
         * unreachable plane costs a price tag, never a usable picker.
         */
        /*
         * Point provider logos at the AI plane, for the same reason the catalog is fetched here: only
         * the console ever called this, so every other surface rendered the fallback glyph beside model
         * names that very much have a brand. The route is on the plane's origin, which is cross-origin
         * from the gateway app — a relative path would ask the wrong server.
         */
        /*
         * TELL THE WORKER WHICH SURFACE THIS IS — the write side of the same fact this manager lists by.
         *
         * `configureAiPlane` sets the platform on the MAIN thread, but every transcript write goes
         * through the AI worker, which holds its own copy of that module and kept the default. So the
         * archive stamped every app's threads as 'ai' while this manager listed its real platform and
         * found nothing — a recents rail that was empty forever in the gateway app and in fleet, and
         * that looked correct in the console only because 'ai' is the default.
         *
         * Pushed from HERE, and not from configureAiPlane, because this object owns `platform` and is
         * the thing that later lists by it: the read side and the write side now come from one field
         * and cannot drift apart again. Fire-and-forget — a failed push costs a mis-stamped thread,
         * never a broken app, and the manager is constructed long before any message can be sent.
         */
        // Optional-called: a worker bundle older than this push has no such method, and the right
        // outcome there is the pre-fix behaviour (the default surface), never a manager that throws on
        // construction and takes the assistant down with it.
        void this.ai.api.setPlatform?.(this.platform)?.catch(() => undefined);
        setProviderLogoBase(`${this.wire?.aiUrl ?? ''}/api/provider-logos`);
        void loadModelCatalog(this.wire?.aiUrl ?? '').catch(() => undefined);
        // Re-read the org's defaults whenever someone changes them, in this tab or another surface of
        // this app. Cross-TAB changes are picked up by the handshake on the next rail-open.
        aiConfigEvents.addEventListener('change', () => {
            if (this.started)
                void this.start();
        });
        /*
         * ANNOUNCE AT BOOT, not only at rail-open.
         *
         * A surface declares its tools before it constructs this manager — app-shell registers the
         * model, symbol, program and dashboard tools, THEN news up the manager — so the change-watcher
         * above, attached during this construction, has already missed that first burst. Left there, the
         * app's tools reached the config page only after someone opened the assistant (the handshake
         * sends the full set), which is exactly the "why don't the specialist tools show?" gap. This one
         * kick announces whatever is already registered, so an app's capabilities are governable from the
         * moment it loads, default-off and visible, whether or not anyone ever opens the rail. Debounced
         * through the same timer, so a registration landing in the same tick coalesces into one call, and
         * an empty registry announces nothing (the server records only a non-empty set).
         */
        this.scheduleAnnounce();
    }
    /** The open conversation, or null. Read by the panel. */
    get openConversation() {
        return this.conversation;
    }
    /**
     * Where to SEND somebody who wants the console — which is not where this manager's API calls go.
     *
     * The API base is same-origin on a dev host, because the other planes are served through the vite
     * proxy. Using it for a link appended the console's path to whichever app the reader was already
     * in, so "AI console" and "Memories" could never load from the fleet or gateway apps. `surfaceUrl`
     * answers the link question; falling back to the API base is right only when this app IS the
     * console, where same-origin genuinely is the console.
     */
    get consoleUrl() {
        return surfaceUrl('ai') || (this.platform === 'ai' ? (this.wire?.aiUrl ?? '') : '');
    }
    /**
     * Open a thread — a new one, or an existing one resumed.
     *
     * Resuming HYDRATES from the archive before returning, so the transcript is whole the moment it
     * renders rather than filling in underneath the reader. That is the seam the store's own comment
     * called the next step: "hydrate the mirror from GetTranscript on construction — the read door
     * already exists."
     */
    async open(conversationId, opts) {
        /*
         * OPENING A THREAD IS USING THE AI, so this is where "idle-until-used" ends.
         *
         * The handshake is what delivers the SERVER tools — web search, sub-agent spawn, agent notes,
         * every connected MCP tool: twenty of them on a typical org. Hosts used to trigger it from the
         * rail's open event, which is one of the ways a person reaches the assistant and not the only
         * one: the home page's hero composer sends a message straight into a thread without ever
         * opening the rail. Observed live on app.vitesseautomation.ai — a full conversation, answered,
         * with connState 'connecting' and ZERO server tools. Asked to search the web the assistant
         * correctly said it could not, which reads as a permission problem and is not one.
         *
         * Starting HERE covers every entry point at once, because none of them can produce a turn
         * without a thread to put it in. `start()` is guarded by its own `started` flag, so the hosts
         * that already call it on rail-open stay correct and this is a no-op for them.
         *
         * NOT awaited: a handshake must never be on the path between pressing Send and seeing the
         * question appear. The turn's tool list is read when the model is called, so a session that
         * lands during the first turn is picked up by it; one that lands after is picked up by the next.
         * The alternative — blocking the open — trades a silent capability gap for a visible stall,
         * which is a worse trade on the surface people type into first.
         */
        if (!this.started)
            void this.start().catch(() => undefined);
        /*
         * THE THREAD ALREADY OPEN IS HANDED BACK, not rebuilt.
         *
         * The panel's stack view opens a thread in one column and may open the same id again in
         * another — most of all when the composer creates a fresh thread and the column that shows it
         * is mounted a frame later. A second object for the same id would hydrate an empty archive over
         * the one that is streaming, and lose the model, effort and specialist set on the first.
         *
         * AFTER THE IMPORT, so nothing awaits between this check and `this.conversation = conv` below.
         * It sat before it, and two opens in flight together both passed: on a reload mid-answer the
         * interrupted question was asked again by an object nobody was looking at, and its answer
         * reached the archive but never the screen.
         */
        const { Conversation } = await import('./conversation');
        if (conversationId && this.conversation?.id === conversationId)
            return this.conversation;
        const store = this.transcriptStore();
        const conv = new Conversation(this.conversationOptions(store, conversationId ?? ''), conversationId);
        // The per-conversation pick, applied before the first turn can be sent.
        if (opts?.model)
            conv.model = opts.model;
        /*
         * PAINT FIRST, FILL SECOND — opening a thread must not wait on the archive.
         *
         * This used to await `hydrate` before handing the conversation over, so clicking a thread
         * showed the OLD one until a network round-trip to /conv/history came back and only then swapped
         * the whole view at once. On anything but a local connection that is a visible stall on the one
         * interaction people make most, and it reads as the app being slow rather than as data loading.
         *
         * The conversation object is live and correct the instant it exists — it just has no past yet —
         * so it is handed over immediately and the past arrives as a second change event. `hydrating`
         * lets the view draw the thread's own skeleton meanwhile instead of an empty room.
         *
         * Ordering is safe because `hydrate` REPLACES and refuses once the thread is live: a turn sent
         * before the archive lands wins, which is the right precedence — what the person just typed is
         * never displaced by history.
         */
        this.conversation = conv;
        if (conversationId) {
            conv.hydrating = true;
            this.dispatchEvent(new CustomEvent('change'));
            /*
             * PAINT FROM CACHE WHILE THE ARCHIVE IS IN FLIGHT.
             *
             * Both are started here and the archive is NOT awaited for the cache to be applied, so the two
             * race and the fast one wins the first frame. Ordering is what makes that safe rather than
             * lucky: the cache is applied as a CACHE — `applyCached`, not `hydrate` — and the archive
             * read that follows replaces a cached snapshot while still refusing to overwrite live turns.
             *
             * That distinction is the whole correctness of this race, and it was missing. `hydrate` is a
             * one-shot: it returns early the moment any turn exists. So whichever landed first won, and a
             * local read beats a round trip essentially always — leaving the transcript frozen at
             * whatever the cache last saw. Measured on a finished sub-agent: thirty messages in the
             * archive, two in the cache from when it was opened mid-flight, and two on the screen.
             *
             * The guard is still `conv.turns.length`: a thread that has since gone live (the person typed
             * while this was in flight) must not have a stale transcript laid over what they just said.
             */
            void cachedTranscript(this._account, conversationId).then((hit) => {
                if (!hit || !conv.hydrating || conv.turns.length)
                    return;
                conv.applyCached([...hit.turns]);
                if (hit.title)
                    this.dispatchEvent(new CustomEvent('titled', { detail: { title: hit.title } }));
                this.dispatchEvent(new CustomEvent('change'));
            });
            void this.hydrate(conv, conversationId).finally(() => {
                conv.hydrating = false;
                this.dispatchEvent(new CustomEvent('change'));
            });
            return conv;
        }
        this.dispatchEvent(new CustomEvent('change'));
        return conv;
    }
    /** Debounced push of the CURRENT client-tool registry to the plane, so the config page sees a tool
     *  the day it is declared without a reload. Shared by the change-watcher and the boot kick below. */
    scheduleAnnounce() {
        clearTimeout(this.announceTimer);
        this.announceTimer = setTimeout(() => void this.ai.api.announceTools(this.platform, clientToolAnnouncements()), 500);
    }
    /** Stop announcing. A manager outlives most views, so this matters only when a whole app tears
     *  down — but a listener on a module-level registry is exactly the kind that survives forever. */
    dispose() {
        clearTimeout(this.announceTimer);
        this.stopWatchingTools();
        this.mentionsOff();
    }
    /**
     * Everything the model may call this turn: the server's tools and this browser's, as one list.
     *
     * The model is not told which is which, and must not be — where a tool runs is our problem. It
     * matters only to `dispatchTool` below, which has to route the call.
     */
    toolDeclarations() {
        // CLIENT FIRST, so a name collision resolves the same way dispatch does. If a UI declares a
        // tool named like a server RPC, the browser will run the local one — and offering the server
        // schema for a call the browser intercepts would describe the wrong parameters to the model.
        const client = clientToolDeclarations();
        const taken = new Set(client.map((d) => d.function.name));
        const server = this.session.serverTools.filter((d) => {
            const name = d?.function?.name;
            // A stand-in for a tool this page declares is the same question asked twice; the page's own
            // answer wins, for the same reason a name collision does.
            return typeof name === 'string' && !taken.has(name) && !taken.has(this.session.standsIn[name] ?? '');
        });
        /*
         * SORTED, AND THAT IS NOT COSMETIC — it is a cache-hit protection.
         *
         * Tool definitions sit in the cached prompt prefix, and CHANGING THEM AT ALL invalidates the
         * whole cache. A cache write costs 1.25x and a read 0.1x, so a needlessly-churning tool list is
         * a 12.5x multiplier on every prefix token, every turn, forever.
         *
         * Registration order is not stable: it is whichever view mounted first, so navigating between
         * two screens that both register tools could reorder the same set and silently pay for a fresh
         * cache write each time. Sorting makes the list a function of WHAT is registered rather than
         * of the order it happened in. (The MCP spec added deterministic tool ordering for exactly this
         * reason.) Client-before-server is preserved because it is load-bearing for collisions above.
         */
        const byName = (a, b) => a.function.name < b.function.name ? -1 : a.function.name > b.function.name ? 1 : 0;
        return [...client.sort(byName), ...server.sort(byName)];
    }
    static { this.MAX_IDENTICAL_FAILURES = 2; }
    repeatKey(runId, name, argumentsJson) {
        return `${runId}\u0000${name}\u0000${argumentsJson}`;
    }
    noteOutcome(key, outcome) {
        if (outcome.ok)
            this.failedTwice.delete(key);
        else
            this.failedTwice.set(key, (this.failedTwice.get(key) ?? 0) + 1);
        /*
         * THE SAME CALL, THE SAME ANSWER, SAID SO.
         *
         * Three identical history queries in one turn, each answered "no samples in that window",
         * observed — the model reads a result it did not like and asks again as if the plant might
         * relent. Nothing here refuses the repeat (a live value legitimately gets re-read), but an
         * answer that came back word for word is told it did, which is the one fact that ends the loop.
         */
        const before = this.lastAnswer.get(key);
        this.lastAnswer.set(key, outcome.message);
        if (outcome.ok && before !== undefined && before === outcome.message) {
            return { ...outcome, message: `${outcome.message} (This is the same call with the same arguments as before in this run, and the answer has not changed — do not repeat it; use what you have or ask something different.)` };
        }
        return outcome;
    }
    /*
     * `ctx` names the run AND ITS THREAD. Both used to be read off `this.conversation` — the thread
     * the panel happened to have OPEN — so a sub-agent's question was stamped with whichever
     * conversation the reader was looking at when the model asked it: the prompt drew under the
     * parent thread, the card in the agent's said "waiting" with nothing to answer, and the header
     * kept counting because the run the question named was not the one that was blocked. The run
     * being stepped is the only thing that knows; the loop hands it over.
     */
    async dispatchTool(call, ctx = { runId: '', epoch: 0n, conversationId: '' }) {
        const repeatKey = this.repeatKey(ctx.runId, call.name, call.argumentsJson);
        if ((this.failedTwice.get(repeatKey) ?? 0) >= AiManager.MAX_IDENTICAL_FAILURES) {
            return {
                ok: false,
                message: `"${call.name}" has already failed twice with these exact arguments, so it was not sent again. ` +
                    `Repeating it will not change the answer. Either change something about the call, or stop and ` +
                    `tell the user what failed and what you would need in order to continue.`,
            };
        }
        /*
         * A CLIENT TOOL IS AUDITED HERE, because nowhere else can be.
         *
         * The server path writes its own trail around `RunTool`; a tool that runs in this browser never
         * reaches it, so until this existed half the AI's capabilities — the half that acts on the app —
         * left no record at all. Both phases, so a tab closed mid-call reads as UNKNOWN rather than as
         * nothing having happened.
         *
         * Not awaited: the report is a record of the call, not part of it.
         */
        const startedAt = Date.now();
        /*
         * ASK A PERSON FIRST, when the session says this one is gated — and BEFORE the client branch,
         * because a client tool can be Ask-tier too. The gate used to sit after it, which meant a rule
         * setting a UI capability to Ask was silently ignored: the local dispatch ran before the
         * question was ever posed. The worker still refuses an unconfirmed SERVER call regardless —
         * this prompt is not the boundary — but for a client tool this IS the enforcement, because the
         * browser is where the tool runs.
         *
         * `confirmedBy` sent to the server is a SIGNAL that a person clicked yes, not the audit
         * identity: the worker stamps the session's own user over it, so a page cannot put words in
         * the audit trail.
         */
        let confirmedBy = '';
        if (this.session.confirmTools.includes(call.name)) {
            const approved = await this.awaitConfirm(call, ctx.runId);
            if (!approved) {
                const refused = {
                    ok: false,
                    message: `"${call.name}" needs a person's approval and did not get it. Do not retry it — ask the user what they would like to do instead.`,
                };
                /*
                 * A "NO" IS AUDITED TOO. The refusal never reaches the plane (that is the point of it), so
                 * without this record the trail showed only the approvals — a person declining a write, the
                 * one event an auditor most wants to see, left nothing. Both phases, as for any client
                 * call: the row says what was asked, and that nobody said yes.
                 */
                void this.ai.api.reportToolCall({ id: call.id, name: call.name, argsJson: call.argumentsJson, conversationId: ctx.conversationId });
                void this.ai.api.reportToolCall({
                    id: call.id,
                    name: call.name,
                    ok: false,
                    message: refused.message,
                    durationMs: Date.now() - startedAt,
                    conversationId: ctx.conversationId,
                    runId: ctx.runId || (this.conversation?.currentRunId ?? ''),
                });
                return refused;
            }
            confirmedBy = 'user';
        }
        if (isClientTool(call.name)) {
            void this.ai.api.reportToolCall({
                id: call.id,
                name: call.name,
                argsJson: call.argumentsJson,
                conversationId: ctx.conversationId,
                confirmedBy,
                // The run on BOTH phases. The dispatch used to arrive without it, and a dispatch that
                // lands first creates the row — so the row had no run to join to, and the console's run
                // line read "used no tools" beside a count of five.
                runId: ctx.runId || (this.conversation?.currentRunId ?? ''),
            });
            // The thread and the run this call belongs to, so a tool that blocks can name both.
            const local = await dispatchClientTool(call.name, call.argumentsJson, {
                conversationId: ctx.conversationId,
                runId: ctx.runId,
                // On screen means THIS thread is the open one — a background tree's calls do not move anybody.
                foreground: !!ctx.conversationId && ctx.conversationId === this.conversation?.id,
            });
            if (local) {
                // The thread travels on BOTH phases: an outcome that lands before its dispatch (routine for
                // a millisecond tool) creates the row, and a row created without its conversation could not
                // be found from the thread it belonged to.
                void this.ai.api.reportToolCall({
                    id: call.id,
                    name: call.name,
                    ok: local.ok,
                    message: local.message,
                    durationMs: Date.now() - startedAt,
                    conversationId: ctx.conversationId,
                    confirmedBy,
                    runId: ctx.runId || (this.conversation?.currentRunId ?? ''),
                });
                return this.noteOutcome(repeatKey, local);
            }
            // Unregistered between the check and the dispatch — rare, and it self-corrects: the server
            // path below carries the same call id, so the row it resolves is this one. It is left saying
            // `source: client`, which is what we believed at the time and is the honest record of it.
        }
        try {
            // THE RUN AND ITS FENCE come from the STEP, not from the conversation. A conversation-level
            // run id is whatever was current when the turn opened; the epoch changes hands with the lease,
            // so a stale one is a fenced write presenting as a tool that mysteriously stopped working.
            // THE THREAD comes from the step too: a subagent's call must land in the subagent's
            // conversation, not in whichever thread happens to be open — a blob stored under the open
            // thread is one the child's own transcript then cannot reference.
            const outcome = await this.ai.api.runTool(call.name, call.argumentsJson, call.id, ctx.conversationId || (this.conversation?.id ?? ''), confirmedBy, ctx.runId || (this.conversation?.currentRunId ?? ''), ctx.epoch);
            if (call.name === 'ai_admin_find_mcp_tools' && outcome.ok)
                this.adoptFoundTools(outcome.data);
            return this.noteOutcome(repeatKey, outcome);
        }
        catch (e) {
            return this.noteOutcome(repeatKey, {
                ok: false,
                message: `"${call.name}" could not be reached: ${e instanceof Error ? e.message : String(e)}. This is a connection problem, not a mistake in the call.`,
                retryable: true,
            });
        }
    }
    /*
     * DEFERRED-MCP EXPANSION — the browser half of FindMcpTools.
     *
     * Past a threshold the worker declares no MCP tools; the model searches, and the tools the search
     * returns are merged into this session's declarations HERE, so the very next step can call them
     * by name. This is the same shape as Anthropic's Tool Search Tool, implemented in our loop so it
     * works on every provider. Deduped by name; a re-handshake (config change) rebuilds the session's
     * list and correctly forgets expansions — the model simply searches again, against the new policy.
     *
     * The one real cost: an expansion changes the declaration list mid-conversation, which invalidates
     * the provider's prompt-cache prefix once. That is the price of not paying fifty declarations
     * every turn, and it is paid only on turns that actually reached for an integration.
     */
    adoptFoundTools(data) {
        const found = data?.tools;
        if (!Array.isArray(found) || !found.length)
            return;
        const have = new Set(this.session.serverTools.map((d) => d?.function?.name).filter(Boolean));
        const fresh = found
            .filter((t) => typeof t.name === 'string' && t.name.startsWith('mcp__') && !have.has(t.name))
            .map((t) => {
            let parameters = { type: 'object', properties: {} };
            try {
                if (t.parametersJson)
                    parameters = JSON.parse(t.parametersJson);
            }
            catch {
                /* an unparseable schema still leaves the tool callable with free-form args */
            }
            return { type: 'function', function: { name: t.name, description: t.description ?? '', parameters } };
        });
        if (!fresh.length)
            return;
        this.session = { ...this.session, serverTools: [...this.session.serverTools, ...fresh] };
        this.dispatchEvent(new CustomEvent('change'));
    }
    get runTranscript() {
        this._runTranscript ??= this.newRunTranscript();
        return this._runTranscript;
    }
    /** A transcript of its own, for a surface that reads one run — the stack view shows several
     *  agents side by side, and one shared transcript could only hold one of them. */
    newRunTranscript() {
        return new RunTranscript((id, cursor, limit) => this.ai.api.history(id, cursor, limit));
    }
    /**
     * The tree a reader is BROWSING, as opposed to one a conversation is driving.
     *
     * Separate from `Conversation.tree()` on purpose, and the difference is ownership rather than
     * shape. A conversation owns the trees IT spawned: it follows them, harvests their returns, and
     * keeps them for the life of the thread. A reader opening a nested agent from a transcript is
     * looking at a tree that may belong to a conversation that is not open — possibly to no open
     * conversation at all — so there is nobody to ask for it.
     *
     * ONE instance, re-pointed. `watch` is idempotent on the same root and the view stops it on
     * unmount, so browsing from tree to tree costs one poll at a time rather than one per tree ever
     * visited. It does not drive: browsing a tree must not start advancing runs the reader only meant
     * to look at.
     */
    get runTree() {
        if (!this._runTree) {
            this._runTree = new RunTree();
            // An owner's stop names every run it ended; their write cards are declined with them.
            this._runTree.addEventListener('stopped', (e) => this.declineConfirms(e.detail));
        }
        return this._runTree;
    }
    /**
     * Fork a thread at a point — a new conversation carrying everything up to and including `seq`.
     *
     * The server copies messages, chapters and blobs; the original is untouched, which is what makes
     * this safe to offer on a message rather than behind a confirmation. Returns the new id, or ''
     * when the plane refused — the caller then stays where it is rather than navigating to a thread
     * that was not created.
     */
    /** Branch: a version of the thread with one message re-asked — its text edited, its attachments
     *  kept. Refreshes the list so the switcher sees the new sibling immediately. */
    async branchConversation(conversationId, seq, text, files = [], references = [], settings) {
        const id = await this.ai.api.branchConversation(conversationId, seq, text, [...files], [...references], settings);
        if (id)
            await this.conversations.refresh();
        return id;
    }
    /** Give a thread a title the person chose. Announced with the same `titled` event an automatic
     *  title uses, so the header changes the same way; the list refresh moves the recents row. */
    async renameConversation(conversationId, title) {
        const ok = await this.ai.api.renameConversation(conversationId, title);
        if (ok) {
            this.dispatchEvent(new CustomEvent('titled', { detail: { title } }));
            await this.conversations.refresh();
        }
        return ok;
    }
    async forkConversation(conversationId, seq) {
        const id = await this.ai.api.forkConversation(conversationId, seq);
        if (id)
            await this.conversations.refresh(); // it is a thread now; the recents list should say so
        return id;
    }
    /** Pin a point as a chapter. Navigation, so a refusal is worth reporting quietly and never worth
     *  blocking on. */
    /** The thread's chapters, oldest position first. */
    async listChapters(conversationId) {
        return this.ai.api.listChapters(conversationId);
    }
    /**
     * Segment a thread THIS TAB IS NOT DRIVING.
     *
     * A thread the browser drives re-segments on a stride inside `send()`. Nothing in a sub-agent's
     * thread goes through `send()` — the worker writes every turn of it — so it reaches that cadence
     * never and carries no contents at all, however long the agent ran. This is the same request the
     * stride makes, for the threads that can only be asked from outside.
     */
    async segmentConversation(conversationId) {
        await this.ai.api.autoChapters(conversationId);
        this.dispatchEvent(new CustomEvent('chapters', { detail: { conversationId } }));
    }
    async pinChapter(conversationId, title, seq) {
        return this.ai.api.addChapter(conversationId, title, seq);
    }
    /** Remove a thread for good, and drop it from the list. */
    async deleteConversation(conversationId) {
        const ok = await this.ai.api.deleteConversation(conversationId);
        if (ok) {
            if (this.conversation?.id === conversationId)
                this.close();
            // AND FORGET IT LOCALLY. A cache that can paint a deleted thread back onto the screen the next
            // time somebody opens its id is worse than no cache: the row is gone from every list and the
            // words are still there.
            forgetTranscript(this._account, conversationId);
            await this.conversations.refresh();
            // AFTER the server confirmed, never before. Announcing optimistically removes the row from
            // every other list on a request that then fails, and those lists have no way to find out.
            conversationsDeleted([conversationId]);
        }
        return ok;
    }
    /** Leave the open thread. The transcript stays in the archive — this only stops showing it. */
    close() {
        this.conversation = null;
        this.dispatchEvent(new CustomEvent('change'));
    }
    /**
     * Fill a resumed conversation from the archive, oldest first.
     *
     * One page, one fold, one hand-off. `hydrate` REPLACES rather than appends and refuses once the
     * thread is live, so this is a one-shot at open time — the only moment the archive is authoritative
     * over the live object.
     */
    /**
     * Re-read a thread this tab is watching, not driving — a sub-agent's, opened from its run.
     *
     * There is no live channel for one transcript: LIVE_TOPIC_TRANSCRIPTS is the conversation LIST,
     * and AUDITS says "a run advanced" without saying what it wrote. So a watched thread was a
     * snapshot taken when it opened. An agent could fail a write while you were looking straight at
     * it and you would not see it until you left the page and came back.
     *
     * Nothing else changes: AUDITS already broadcasts on every step of every run and nothing was
     * listening. This is the listening half, and it re-reads rather than patches, because the archive
     * is what knows — this tab wrote none of it.
     */
    async refreshWatched(conversationId) {
        // The one currently open. A watched thread is by definition the one being LOOKED at, and the
        // manager holds exactly one open conversation — so anything else is not on screen and re-reading
        // it would be work for nobody.
        const conv = this.conversation;
        if (!conv || conv.id !== conversationId)
            return;
        const page = await this.ai.api.historyTail(conversationId);
        // A read that failed is not a thread that emptied. Applying an empty page over a full one is
        // what made a thread someone was reading vanish into a skeleton until the next tick.
        if (!page)
            return;
        if (!page.messages.length && conv.turns.length)
            return;
        // The thread may have moved on while the read was in flight; writing then would put one
        // conversation's rows into another's view.
        if (this.conversation !== conv)
            return;
        conv.rehydrateWatched(foldTranscript(page.messages));
    }
    async hydrate(conv, conversationId) {
        const page = await this.ai.api.history(conversationId);
        // The fold — which turn owns a tool call, which rows are not turns at all — lives in
        // `transcript-fold`, because the run-keyed transcript view renders the same archived rows.
        conv.hydrate(foldTranscript(page.messages));
        /*
         * THE THREAD'S NAME ARRIVES WITH THE THREAD.
         *
         * The header used to have exactly one source for it — the recents list — which is ONE PAGE,
         * filtered by platform and by the selected origin pill. A thread outside that filter had no
         * name in its own header, and on a fresh load the restore raced the list, so whether a reload
         * showed the name depended on which fetch won. The panel already re-runs the lookup when the
         * list lands, which fixed the race but not the dependency.
         *
         * This removes the dependency: the response for the thread you opened carries the thread's own
         * title, so the header is right whether or not the row is in the rail. Same 'titled' event the
         * live path uses, so the panel needs no second way to receive one.
         */
        if (page.title) {
            // AND IT IS ALREADY NAMED, so nothing should name it again. The auto-title latch lives on the
            // conversation OBJECT, and a resumed thread gets a fresh one — so without this the next
            // message the person sends renames a thread that already had a name, including one they typed
            // themselves.
            conv.markTitled();
            this.dispatchEvent(new CustomEvent('titled', { detail: { title: page.title } }));
        }
        /*
         * REMEMBER IT AS IT NOW READS — the FOLD, not the rows.
         *
         * Folding is where "which turn owns this tool call" is decided; caching the folded turns rather
         * than the archived messages means the next open saves a parse as well as a round trip. Written
         * here rather than on every message: a live thread is already on screen, and a write per
         * completed stream would spend storage caching something nobody is about to reload.
         */
        rememberTranscript(this._account, conversationId, { turns: conv.turns, title: page.title });
    }
    /**
     * This surface's recent threads, one page at a time.
     *
     * On the manager rather than called from the view: the Cardinal Rule — a view reads state, a
     * manager does IO. The platform comes from the manager's own construction, so a view cannot ask
     * for another app's threads even by mistake.
     */
    /**
     * The bytes of one artifact in a conversation — a chart the assistant drew, a file it produced.
     *
     * On the manager because the download route is authed and the token lives on the plane: a view
     * that fetched it itself would be doing IO with a credential it should not hold (the Cardinal
     * Rule, and the reason `readConvFile` is a plane method rather than a URL anyone can build).
     */
    readArtifact(conversationId, sha256) {
        return this.ai.api.readConvFile(conversationId, sha256);
    }
    listConversations(cursor = '') {
        return this.ai.api.listConversations(this.platform, cursor);
    }
    /** Which surface this is. Read by anything that persists per-app state — see session-memory. */
    get surface() {
        return this.platform;
    }
    /**
     * WHOSE THREADS THESE ARE — set by the host, and the transcript cache is off until it is.
     *
     * Nothing on the session identifies the account: the server scopes conversations by the token, so
     * the client has never needed to. A local cache does, and for one reason — two accounts on one
     * browser must not read each other's threads out of a shared store.
     *
     * Absent means NO CACHING rather than a shared bucket. Fail-closed is the only defensible default
     * for a store whose failure mode is showing one person another person's conversation; a host that
     * wants the speed says who it is.
     */
    set account(id) {
        const next = id.trim();
        if (next === this._account)
            return;
        this._account = next;
        // Signing in is also the moment to drop whoever was here before. Keys are user-prefixed, so
        // nothing has to be remembered at sign-OUT for the next account to be safe.
        if (next)
            sweepTranscripts(next);
    }
    get account() {
        return this._account;
    }
    /** The current AI session (connState + minted token + resolved config). Read by the AI panel. */
    get current() {
        return this.session;
    }
    /** Handshake with the AI plane. Re-runnable (a re-handshake after sign-in / token refresh);
     *  shows 'connecting' until the reply, then the resolved session (or a graceful error state).
     *
     *  TEXT ONLY — no `voice` argument, so the server reserves no credit. A voice session is opened by
     *  `VoiceWindowKeeper` (agent-loop), which drives this same handshake with a modality and settles
     *  each window as it renews. Opening voice from here would buy a window on every page load. */
    /** The archive writer every thread this tab drives writes through — one construction. */
    transcriptStore() {
        return durableTranscriptStore({
            /*
             * EVERY ARGUMENT, forwarded. This took three of six.
             *
             * `reasoning`, `reasoningMs` and `telemetry` were dropped here, silently, on every write — so
             * the thinking a turn did and what it measured never reached the archive at all. Both halves
             * of the round trip existed and worked: the store offers them, the door persists them, and
             * `hydrate` rebuilds the header's TTFT, average and running cost from them on resume. They
             * simply never got past this line, which is why a reopened thread came back with an empty
             * header and no disclosures, and why that looked like a resume bug rather than a write one.
             *
             * Written point-free rather than by naming the parameters: a truncated arrow is exactly the
             * shape that caused this, and it compiles, because JavaScript lets a function ignore arguments
             * it was handed.
             */
            appendMessage: (...args) => this.ai.api.appendMessage(...args),
            // And the same trap again, one method later: a finished agent's answer written through the
            // ordinary append lost its author and its stable id, so a reopened thread drew it as the
            // reader's own words and every reopen reported it afresh. Everything else was in place — the
            // store offers it, the door records it, the fold reads it back — and it died on this list.
            appendReturn: (...args) => this.ai.api.appendReturn(...args),
            appendToolCalls: (id, calls, reasoning, reasoningMs) => this.ai.api.appendToolCalls(id, calls, reasoning, reasoningMs),
            appendToolResult: (id, callId, name, outcome) => this.ai.api.appendToolResult(id, callId, name, outcome),
            // The attachment path, both directions: up at send, back at resume so the prompt still
            // carries the text of a file attached before the reload.
            upload: (id, file) => this.ai.api.uploadConvFile(id, file),
            readFile: (id, sha) => this.ai.api.readConvFile(id, sha),
            // The archive, for a run adopted from elsewhere — see durableTranscriptStore.history. The
            // tail, because a step needs what the run did last; the door caps the page at its own size.
            readHistory: async (id) => (await this.ai.api.historyTail(id, AiManager.ADOPTED_HISTORY_ROWS))?.messages ?? null,
        });
    }
    /** What a conversation — or a step driving an adopted tree — needs from this manager. `conversationId`
     *  is '' for the latter: the store takes the id per call, and the title hooks then have nothing to
     *  touch. */
    conversationOptions(store, conversationId) {
        return {
            aiUrl: this.wire?.aiUrl ?? '',
            token: this.wire?.token ?? (async () => null),
            // The surface this manager was built for. It was already used to key config and tool
            // announcements; the model calls left it out, so every call in the fleet was recorded as
            // the console's.
            platform: this.platform,
            // The roster the handshake resolved for this surface. A function, not a snapshot: a
            // re-handshake replaces the session, and a captured array would pin the old one.
            experts: () => this.session.experts,
            store,
            // A conversation is TEXT, so its default is the text model — the realtime one is voice's.
            defaultModel: this.session.textModel || this.session.model || undefined,
            // THE UNION, assembled per turn.
            //
            // Not captured once: a UI registers tools when a view mounts and drops them when it leaves,
            // so the set is a function of what is on screen right now. A conversation that snapshotted
            // it at open() would keep offering a tool whose view is gone, and would never learn one
            // registered a second later — which is precisely the property that makes a new tool work
            // the day it is written.
            tools: () => this.toolDeclarations(),
            dispatch: (call, ctx) => this.dispatchTool(call, ctx),
            // A stopped turn's write cards go with it. See `declineConfirms`.
            onStopped: (runId) => this.declineConfirms([runId]),
            autoTitle: (id) => this.ai.api.autoTitle(id),
            // Where the person is goes with it, read HERE: the plane's client can run in a worker, which
            // has no page to read (see `screenHint`).
            suggestNext: (id) => this.ai.api.suggestNext(id, screenHint()),
            completeDraft: (id, draft) => this.ai.api.completeDraft(id, draft, screenHint()),
            /*
             * Segmenting is only half of it — whoever is SHOWING the contents has to be told, or the
             * chapters the model just found sit in the archive until the thread is reopened. The panel
             * loads them on open and after a manual pin; this is the third way they change.
             */
            autoChapters: async (id) => {
                await this.ai.api.autoChapters(id);
                this.dispatchEvent(new CustomEvent('chapters', { detail: { conversationId: id } }));
            },
            onTitled: (title) => {
                // The thread now has a name: tell the view, and refresh the list so the recents entry
                // stops saying "Untitled" without the user having to go and come back.
                this.dispatchEvent(new CustomEvent('titled', { detail: { title } }));
                void this.conversations.refresh();
                // And tell every OTHER list. A thread named here reads "Untitled" in the console until
                // someone reloads it — the same disagreement deletes had, in the other direction.
                conversationsTouched(conversationId ? [conversationId] : []);
            },
        };
    }
    async start() {
        this.started = true;
        this.session = { ...this.session, connState: 'connecting' };
        this.dispatchEvent(new CustomEvent('change'));
        // The declarations are read HERE, on the main thread, because that is the only place the
        // registry exists — the api runs in the Backend Worker and would read an empty one.
        this.session = await this.ai.api.handshake(this.platform, undefined, clientToolAnnouncements());
        this.dispatchEvent(new CustomEvent('change'));
        /*
         * AND PICK UP WHAT AN EARLIER TAB LEFT RUNNING.
         *
         * A tree is durable and adoptable by design — the driver hands its lease back on close, the
         * server offers orphans to whoever asks — and NOTHING ASKED. `resumeAbandonedWork` existed and
         * had no caller, so a reload mid-run left every agent it had started sitting at "Running" until
         * its lease lapsed, and then forever: nobody came back for it. Observed as a run that stayed
         * alive in the header for six minutes with an empty transcript under it.
         *
         * Asked once the session is up, and again on a cadence: a lease outlives its tab by up to its
         * TTL, so the first ask after a reload may be refused, and another tab may die at any time.
         */
        void this.resumeOrphans();
        this.orphanTimer ??= setInterval(() => void this.resumeOrphans(), AiManager.ORPHAN_SWEEP_MS);
    }
    /** How often this tab offers to adopt trees nobody is driving. Longer than a lease TTL, so a
     *  driver that is merely slow to renew is not fought over. */
    /** How much of an adopted run's past its next step carries — the archive's page cap. */
    static { this.ADOPTED_HISTORY_ROWS = 200; }
    static { this.ORPHAN_SWEEP_MS = 45_000; }
    /**
     * Adopt every tree this user owns that no driver is advancing, and drive it to completion with
     * the same step a conversation would — same tools, same door, same roster, same archive.
     */
    async resumeOrphans(resume = resumeAbandonedWork) {
        if (!this.wire)
            return; // nothing to drive with — this manager only lists
        const { browserStep, TAB_DRIVE_BUDGET_USD } = await import('./conversation');
        const store = this.transcriptStore();
        try {
            await resume(browserStep(this.conversationOptions(store, ''), store), TAB_DRIVE_BUDGET_USD);
        }
        catch {
            // Refusals and transport failures are expected here — another tab may have adopted first.
        }
    }
}
/**
 * Pull display text out of a stored message.
 *
 * The archive keeps the AI-SDK parts structure, not a string — a turn can carry tool calls, files
 * and several text runs. A resumed transcript shows the prose; the richer parts render when the
 * transcript surface grows to handle them.
 */
export function textOf(content) {
    return joinParts(content, 'text');
}
/** The thinking a stored message kept, if any — what a resumed turn's disclosure shows. */
export function reasoningOf(content) {
    // A bare string is prose by definition, never reasoning: `textOf` claims it, this must not, or a
    // plain stored message would show up as both the answer and the thinking behind it.
    return typeof content === 'string' ? '' : joinParts(content, 'reasoning');
}
/**
 * The tool calls a stored message recorded, as the transcript's row shape.
 *
 * The archive keeps them as AI-SDK `tool-<name>` parts carrying the input, the state and — once it
 * resolved — the output. That is everything a row needs, so a resumed turn shows what it ran rather
 * than losing it: before this, hydrate dropped tool-only messages entirely and a reopened thread
 * looked as though the answer had arrived from nowhere.
 *
 * `input-available` maps to `running` deliberately. It means the call was dispatched and its outcome
 * was never written — which after a reload is not "in flight", it is UNKNOWN, and the spinning row
 * is the honest rendering of a call whose result nobody recorded.
 */
export function toolsOf(content) {
    const parts = Array.isArray(content) ? content : content?.parts;
    if (!Array.isArray(parts))
        return [];
    const out = [];
    for (const raw of parts) {
        if (typeof raw?.type !== 'string' || !raw.type.startsWith('tool-') || !raw.toolCallId)
            continue;
        /*
         * THE OUTCOME, NOT ITS ENVELOPE. `appendToolResult` archives a tool's outcome as
         * `{ message, data }` — the same two fields the live path puts on a TurnTool — and this read
         * used to hand the whole envelope back as `data` with no `message` at all. So a reloaded row
         * had lost what the tool SAID: an answered question read "you did not answer", and every
         * settled row was a bare name. The live thread and the reloaded one disagreed about the same
         * record. A bare output (a tool that wrote no envelope) is still the data.
         */
        const env = raw.output;
        const enveloped = !!env && typeof env === 'object' && typeof env.message === 'string';
        const message = raw.errorText || (enveloped ? env.message : '');
        const data = enveloped ? env.data : raw.output;
        out.push({
            id: raw.toolCallId,
            name: raw.type.slice('tool-'.length),
            argumentsJson: raw.input === undefined ? '' : JSON.stringify(raw.input),
            state: raw.state === 'output-available' ? 'ok' : raw.state === 'output-error' ? 'error' : 'unknown',
            ...(message ? { message } : {}),
            ...(data !== undefined ? { data } : {}),
        });
    }
    return out;
}
/**
 * ARTIFACTS A TURN PRODUCED — the chart, the render, the export.
 *
 * `file` parts are written beside a tool result by the archive (see `appendToolResult`): the bytes
 * live in the conversation's blob store and the part carries only the reference, because a
 * transcript that inlined a megabyte of PNG would be re-sent to the model on every later turn for
 * something it cannot read.
 *
 * Read back the same way tool calls are — accumulated across the rows that carry no prose and
 * attached to the turn that eventually speaks — because an artifact belongs to the ANSWER it was
 * produced for, not to a row of its own. A file part with no sha is skipped rather than rendered
 * as a broken tile: the reference is the whole content of the part, so one without it is a record
 * of nothing.
 */
export function filesOf(content) {
    const parts = Array.isArray(content) ? content : content?.parts;
    if (!Array.isArray(parts))
        return [];
    const out = [];
    for (const raw of parts) {
        if (raw?.type !== 'file' || typeof raw.sha256 !== 'string' || !raw.sha256)
            continue;
        out.push({
            sha256: raw.sha256,
            mediaType: typeof raw.mediaType === 'string' ? raw.mediaType : 'application/octet-stream',
            filename: typeof raw.filename === 'string' ? raw.filename : '',
            size: typeof raw.size === 'number' ? raw.size : 0,
        });
    }
    return out;
}
/**
 * One turn's persisted measurements, if the row carries them.
 *
 * What makes a reopened conversation the SAME conversation. These were in-memory only, so the
 * header's TTFT, average and cost emptied on reopen and refilled only after another message.
 */
export function telemetryOf(content) {
    const parts = Array.isArray(content) ? content : content?.parts;
    if (!Array.isArray(parts))
        return undefined;
    const d = parts.find((p) => p?.type === 'data-turn-telemetry')?.data;
    if (!d || typeof d.totalMs !== 'number')
        return undefined;
    return {
        totalMs: d.totalMs,
        // Absent stays absent: a turn nobody timed and a turn that cost nothing are different facts.
        ...(typeof d.ttftMs === 'number' ? { ttftMs: d.ttftMs } : {}),
        ...(typeof d.costUsd === 'number' ? { costUsd: d.costUsd } : {}),
        ...(typeof d.tokens === 'number' ? { tokens: d.tokens } : {}),
        ...(d.stopped === true ? { stopped: true } : {}),
    };
}
/**
 * Who a stored message was asked of or answered by, if it says — see `TurnSettings`. Only string
 * fields that are set: a row from before turns recorded it, or a note an agent wrote, has none.
 */
export function settingsOf(content) {
    const parts = Array.isArray(content) ? content : content?.parts;
    if (!Array.isArray(parts))
        return undefined;
    const d = parts.find((p) => p?.type === 'data-turn-settings')?.data;
    if (!d)
        return undefined;
    const s = {
        ...(typeof d.expertId === 'string' && d.expertId ? { expertId: d.expertId } : {}),
        ...(typeof d.model === 'string' && d.model ? { model: d.model } : {}),
        ...(typeof d.effort === 'string' && d.effort ? { effort: d.effort } : {}),
    };
    return Object.keys(s).length ? s : undefined;
}
/**
 * The agent trees a stored turn STARTED.
 *
 * Read from the spawn tool's own recorded OUTPUT rather than from anything the UI kept, which is
 * what makes the tree — and the button into each agent's chat — survive a reopen. Without this a
 * resumed thread showed the spawn happening and no way to reach what it spawned.
 */
export function spawnedRootsOf(content) {
    const parts = Array.isArray(content) ? content : content?.parts;
    if (!Array.isArray(parts))
        return [];
    /*
     * THE ROOT SITS UNDER `output.data`, NOT ON `output`.
     *
     * A tool outcome is stored as the whole envelope the tool returned — `{ message, data }`, the
     * platform's Result shape — so the ids live at `output.data.rootRunId`. This read looked for
     * `output.rootRunId` and therefore found NOTHING, on every archived row, since it was written.
     *
     * The live path never noticed: `noteSpawn` reads `outcome.data.rootRunId` off the in-memory
     * outcome and is correct. So a spawn showed its tree while the tab stayed open and lost it on
     * reopen — the archive had the id all along and this was looking one level too high. The flat
     * form is still accepted in case anything ever wrote one.
     */
    const roots = [];
    for (const p of parts) {
        if (!p?.type?.startsWith('tool-'))
            continue;
        const root = p.output?.data?.rootRunId ?? p.output?.rootRunId;
        if (typeof root === 'string' && root && !roots.includes(root))
            roots.push(root);
    }
    return roots;
}
/**
 * The root run id a SINGLE spawn call produced, read off that call's own output.
 *
 * `spawnedRootsOf` answers the same question for a whole message and flattens the answer, which
 * loses WHICH call spawned WHICH agent — fine while every tree hung below the turn, wrong now that a
 * tree is drawn inside the row that produced it. The two read the same two shapes, from one place,
 * so a change to where the id lives cannot fix one and miss the other.
 */
/** The run a spawn STARTED, as opposed to the forest it belongs to. The tree is what a canvas
 *  loads; this is what it should be looking at when it gets there. */
// Kept on this module's surface for its existing importers; they live in `spawn-ids` so the turn
// renderer can read them without importing the manager.
export { spawnedRunIdOfTool, rootRunIdOfTool } from './spawn-ids';
/** How long that thinking took, when the row recorded it. UNDEFINED for a row written before the
 *  clock was persisted — the label then reads "Thought" rather than inventing a zero. */
export function reasoningMsOf(content) {
    if (typeof content === 'string')
        return undefined;
    const parts = Array.isArray(content) ? content : content?.parts;
    if (!Array.isArray(parts))
        return undefined;
    const ms = parts.find((p) => p?.type === 'reasoning' && typeof p.ms === 'number')?.ms;
    return typeof ms === 'number' ? ms : undefined;
}
/**
 * Concatenate every part of one type out of a stored message.
 *
 * BOTH SHAPES. The archive stores a BARE parts array — `[{type:'text',text:'…'}]` — while this read
 * only the `{parts:[…]}` wrapper, so every resumed message came back as the empty string and a
 * reopened thread rendered as a column of blank bubbles. The transcript looked destroyed; it was
 * intact the whole time and simply never unwrapped. The worker's own parser accepts both, and so
 * must this.
 *
 * One function for both part types rather than two nearly-identical readers: that unwrapping rule is
 * the part everyone gets wrong, and it should exist once so a second reader cannot learn it wrong.
 */
function joinParts(content, type) {
    if (typeof content === 'string')
        return content;
    const parts = Array.isArray(content) ? content : content?.parts;
    if (!Array.isArray(parts))
        return '';
    return parts
        .filter((p) => p?.type === type && typeof p.text === 'string')
        .map((p) => p.text)
        .join('');
}
/**
 * The page the person has open, as the suggested next reply is given it: its address, title and
 * headings, not its text or selection — enough to say where they are, without sending the screen to a
 * second model on every answer. Empty where there is no page (a test, a worker).
 */
function screenHint() {
    if (typeof document === 'undefined')
        return { path: '', title: '', headings: [] };
    const s = readScreen();
    return { path: s.path, title: s.title, headings: s.headings };
}
//# sourceMappingURL=manager.js.map